import React, { useState, useEffect } from 'react';
import './CoachingPoints.css';
import CoachingMessage from './CoachingMessage';
import CoachingReviewed from './CoachingReviewed';

const CoachingPoints = ({ suggestions }) => {
  const [currentSuggestions, setCurrentSuggestions] = useState([]);
  const [reviewedSuggestions, setReviewedSuggestions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  // Process suggestions data when it changes
  useEffect(() => {
    if (!suggestions) {
      return;
    }
    
    if (!suggestions?.nextSteps?.nextSteps) {
      // Try to handle different data structures
      if (Array.isArray(suggestions)) {
        const convertedSuggestions = suggestions.map((s, index) => {
          if (typeof s === 'string') {
            return {
              suggestion: s,
              section: 'publish',
              priority: 'medium',
              reviewed: false,
              increment: index + 1
            };
          }
          if (typeof s === 'object') {
            return {
              suggestion: s.suggestion || s.content || s.text || JSON.stringify(s),
              section: s.section || 'publish',
              priority: s.priority || 'medium',
              reviewed: s.reviewed || false,
              increment: s.increment || index + 1,
              context: s.context || '',
              reviewedContext: s.reviewedContext || '',
              timeline: s.timeline || null
            };
          }
          return s;
        });
        
        setCurrentSuggestions(convertedSuggestions.filter(s => s.section === 'publish'));
        setReviewedSuggestions(convertedSuggestions.filter(s => s.section === 'reviewed'));
        return;
      }
      
      return;
    }

    const suggestionsArray = suggestions.nextSteps.nextSteps;
    
    // Separate current (publish) and reviewed suggestions
    const current = suggestionsArray.filter(s => s.section === 'publish');
    const reviewed = suggestionsArray.filter(s => s.section === 'reviewed');

    // Ensure all properties are properly set for both current and reviewed suggestions
    const processedCurrent = current.map(s => ({
      ...s,
      reviewed: s.reviewed || false,
      reviewedContext: s.reviewedContext || '',
      timeline: s.timeline || null
    }));

    const processedReviewed = reviewed.map(s => ({
      ...s,
      reviewed: s.reviewed || false,
      reviewedContext: s.reviewedContext || '',
      timeline: s.timeline || null
    }));

    // Set suggestions immediately
    setCurrentSuggestions(processedCurrent);
    
    // Animate loading of new data only if we have more suggestions than before
    if (processedCurrent.length > 0 && processedCurrent.length > currentSuggestions.length) {
      setIsLoading(true);
      setTimeout(() => {
        setIsLoading(false);
      }, 300);
    } else {
      setIsLoading(false);
    }

    setReviewedSuggestions(processedReviewed);
  }, [suggestions, currentSuggestions.length]);

  return (
    <div className="coaching-points-container">
      {/* Current Suggestions */}
      <div className="current-suggestions">
        {isLoading && (
          <div className="loading-indicator">
            <div className="loading-spinner"></div>
            <span>Loading new suggestions...</span>
          </div>
        )}
        
        {currentSuggestions.length > 0 ? (
          currentSuggestions.map((suggestion, index) => (
            <CoachingMessage 
              key={`${suggestion.increment || index}-${index}`}
              suggestion={suggestion}
              isNew={isLoading}
            />
          ))
        ) : (
          <div className="empty-coaching-state">
            <div className="empty-state-icon">
              <i className="ti ti-bulb"></i>
            </div>
            <p className="empty-state-text">
              Coaching tips will automatically appear here as the meeting progresses.
            </p>
          </div>
        )}
      </div>

      {/* Reviewed Suggestions */}
      {reviewedSuggestions.length > 0 && (
        <CoachingReviewed suggestions={reviewedSuggestions} />
      )}
    </div>
  );
};

export default CoachingPoints;
